/**
 * RagChatWidget — admin RAG chatbot drawer.
 *
 * Visibility is driven by the external store (`utils/ragChatStore`) — there is
 * no built-in launcher button. Open it from anywhere via `openRagChat()`.
 *
 * Calls: /api/admin/rag/*  (see backend/routes/admin_rag_chat.py)
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import {
  Sparkles, X, Send, Loader2, Plus, Trash2, MessageSquare, Bot, User as UserIcon,
  Database, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';
import { useRagChatOpen } from '../utils/ragChatStore';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUGGESTIONS = [
  { ar: 'كم عميل لديه تأشيرة تنتهي خلال 30 يوم؟', en: 'How many customers have visas expiring in 30 days?' },
  { ar: 'أعطني آخر 5 طلبات خدمة',                 en: 'Show me the latest 5 service requests' },
  { ar: 'كم بزنس مسجّل في CloudGM؟',              en: 'How many businesses are registered in CloudGM?' },
  { ar: 'كم تكلفة AI لهذا اليوم؟',                 en: "What's today's AI cost?" },
  { ar: 'أعطني أعلى 5 فواتير لم تُدفع بعد',        en: 'Top 5 unpaid invoices' },
  { ar: 'كم خدمة تأشيرة مفعّلة؟',                  en: 'How many active visa services?' },
];

export default function RagChatWidget() {
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [open, openRag, closeRag] = useRagChatOpen();
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const scrollRef = useRef(null);

  // ---------------- API helpers ----------------
  const fetchSessions = useCallback(async () => {
    setLoadingSessions(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/rag/sessions`, { headers: getAuthHeaders() });
      // Dedupe by id — backend may return duplicates from legacy data
      const list = res.data?.data || [];
      const unique = Array.from(new Map(list.map((s) => [s.id, s])).values());
      setSessions(unique);
    } catch (e) {
      console.error('[RAG] fetchSessions failed', e);
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  const fetchMessages = useCallback(async (sessionId) => {
    if (!sessionId) {
      setMessages([]);
      return;
    }
    setLoadingMessages(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/rag/sessions/${sessionId}/messages`, { headers: getAuthHeaders() });
      setMessages(res.data?.data?.messages || []);
    } catch (e) {
      console.error('[RAG] fetchMessages failed', e);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  // Load sessions on first open
  useEffect(() => {
    if (open) fetchSessions();
  }, [open, fetchSessions]);

  // Reload messages when active session changes
  useEffect(() => {
    fetchMessages(activeSessionId);
  }, [activeSessionId, fetchMessages]);

  // Auto-scroll on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // ---------------- Actions ----------------
  const startNewChat = () => {
    setActiveSessionId(null);
    setMessages([]);
    setInput('');
  };

  const handleSend = async (questionOverride) => {
    const question = (questionOverride ?? input).trim();
    if (!question || sending) return;

    setInput('');
    setSending(true);

    // Optimistic user bubble
    const optimisticUser = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: question,
      ts: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimisticUser]);

    try {
      const res = await axios.post(
        `${API_URL}/api/admin/rag/ask`,
        { question, session_id: activeSessionId || undefined },
        { headers: getAuthHeaders(), timeout: 60000 },
      );
      const data = res.data || {};
      const sid = data.session_id;
      const assistantMsg = data.message;

      if (!activeSessionId && sid) {
        setActiveSessionId(sid);
        await fetchSessions();
      } else {
        // refresh sidebar counters
        fetchSessions();
      }
      if (assistantMsg) {
        setMessages((prev) => [...prev, assistantMsg]);
      }
    } catch (e) {
      console.error('[RAG] ask failed', e);
      const err = e.response?.data?.detail || (isAr ? 'فشل الإرسال' : 'Send failed');
      toast.error(err);
      setMessages((prev) => [
        ...prev,
        {
          id: `err-${Date.now()}`,
          role: 'assistant',
          content: isAr
            ? `حصل خطأ أثناء معالجة طلبك: ${err}`
            : `Error processing your request: ${err}`,
          ts: new Date().toISOString(),
          error: true,
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const handleDeleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm(isAr ? 'حذف هذه المحادثة نهائياً؟' : 'Delete this conversation permanently?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/rag/sessions/${sid}`, { headers: getAuthHeaders() });
      toast.success(isAr ? 'تم الحذف' : 'Deleted');
      if (sid === activeSessionId) startNewChat();
      fetchSessions();
    } catch {
      toast.error(isAr ? 'فشل الحذف' : 'Delete failed');
    }
  };

  // ---------------- Render ----------------
  return (
    <>
      {/* Circular floating launcher — bottom-right, persists across all admin pages */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 280, damping: 22 }}
            whileHover={{ scale: 1.08, rotate: 4 }}
            whileTap={{ scale: 0.92 }}
            onClick={openRag}
            data-testid="rag-chat-launcher"
            aria-label={isAr ? 'مساعد البيانات الذكي' : 'AI DB Assistant'}
            title={isAr ? 'مساعد البيانات الذكي' : 'AI DB Assistant'}
            style={{ position: 'fixed', right: '1.5rem', bottom: '1.5rem', zIndex: 60 }}
            className="w-14 h-14 rounded-full bg-gradient-to-br from-[#d4af37] to-[#9b7d20] text-black flex items-center justify-center shadow-[0_10px_36px_rgba(212,175,55,0.5)] hover:shadow-[0_14px_44px_rgba(212,175,55,0.65)] transition-shadow"
          >
            <Sparkles className="h-6 w-6" />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full ring-2 ring-[#0a0a14] animate-pulse" aria-hidden="true" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={closeRag}
            data-testid="rag-chat-drawer"
          >
            <motion.div
              initial={{ y: 40, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 40, opacity: 0, scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 280, damping: 28 }}
              className="bg-[#0a0a14] border border-white/10 sm:rounded-3xl w-full sm:max-w-5xl h-[100vh] sm:h-[85vh] flex overflow-hidden shadow-[0_30px_120px_rgba(0,0,0,0.7)]"
              onClick={(e) => e.stopPropagation()}
              dir={isAr ? 'rtl' : 'ltr'}
            >
              {/* Sidebar — Sessions */}
              <aside
                className={`flex-shrink-0 border-${isAr ? 'l' : 'r'} border-white/10 bg-white/[0.02] flex flex-col transition-all ${
                  sidebarOpen ? 'w-64' : 'w-0 sm:w-12'
                }`}
                data-testid="rag-sidebar"
              >
                <div className={`p-3 border-b border-white/10 flex items-center justify-between ${sidebarOpen ? '' : 'sm:flex-col sm:gap-2'}`}>
                  {sidebarOpen && (
                    <button
                      onClick={startNewChat}
                      data-testid="rag-new-chat-btn"
                      className="flex-1 flex items-center gap-2 px-3 py-2 rounded-xl bg-[#d4af37]/15 hover:bg-[#d4af37]/25 text-[#d4af37] text-sm font-semibold transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                      {isAr ? 'محادثة جديدة' : 'New chat'}
                    </button>
                  )}
                  <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="p-2 rounded-lg hover:bg-white/5 text-[#A1A1AA] hidden sm:block"
                    data-testid="rag-toggle-sidebar"
                    aria-label="Toggle sidebar"
                  >
                    {sidebarOpen ? (isAr ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />) : (isAr ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />)}
                  </button>
                </div>

                {sidebarOpen && (
                  <div className="flex-1 overflow-y-auto p-2 space-y-1">
                    {loadingSessions ? (
                      <div className="flex justify-center py-6">
                        <Loader2 className="h-4 w-4 animate-spin text-[#d4af37]" />
                      </div>
                    ) : sessions.length === 0 ? (
                      <p className="text-center text-xs text-[#71717A] p-4 leading-relaxed">
                        {isAr ? 'لا توجد محادثات بعد. اكتب سؤالاً للبدء.' : 'No chats yet. Ask a question to start.'}
                      </p>
                    ) : (
                      sessions.map((s) => (
                        <div
                          key={s.id}
                          onClick={() => setActiveSessionId(s.id)}
                          data-testid={`rag-session-${s.id}`}
                          className={`group p-2.5 rounded-lg cursor-pointer transition-colors flex items-start gap-2 ${
                            s.id === activeSessionId
                              ? 'bg-[#d4af37]/15 border border-[#d4af37]/30'
                              : 'hover:bg-white/5 border border-transparent'
                          }`}
                        >
                          <MessageSquare className="h-3.5 w-3.5 text-[#A1A1AA] shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-white truncate font-medium">{s.title}</p>
                            <p className="text-[10px] text-[#71717A] mt-0.5">
                              {s.msg_count} {isAr ? 'رسالة' : 'msg'}
                            </p>
                          </div>
                          <button
                            onClick={(e) => handleDeleteSession(s.id, e)}
                            data-testid={`rag-delete-${s.id}`}
                            className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-500/15 text-red-400 transition-all"
                            aria-label="Delete"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </aside>

              {/* Main chat area */}
              <section className="flex-1 flex flex-col min-w-0">
                {/* Header */}
                <header className="px-5 py-3.5 border-b border-white/10 flex items-center justify-between bg-gradient-to-b from-white/[0.03] to-transparent">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-[#d4af37]/15 border border-[#d4af37]/30 flex items-center justify-center shrink-0">
                      <Sparkles className="h-4.5 w-4.5 text-[#d4af37]" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-white font-bold text-sm truncate">
                        {isAr ? 'مساعد قاعدة البيانات الذكي' : 'AI Database Assistant'}
                      </h2>
                      <p className="text-[10px] text-[#71717A]">
                        {isAr
                          ? 'يستعلم عن البيانات بلغة طبيعية (قراءة فقط)'
                          : 'Natural-language DB queries (read-only)'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={closeRag}
                    data-testid="rag-close-btn"
                    className="p-2 rounded-lg hover:bg-white/10 text-[#A1A1AA]"
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </header>

                {/* Messages */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-4" data-testid="rag-messages">
                  {loadingMessages ? (
                    <div className="flex justify-center py-10">
                      <Loader2 className="h-6 w-6 animate-spin text-[#d4af37]" />
                    </div>
                  ) : messages.length === 0 ? (
                    <EmptyState isAr={isAr} onPick={(q) => handleSend(q)} />
                  ) : (
                    messages.map((m) => <Bubble key={m.id} msg={m} isAr={isAr} />)
                  )}
                  {sending && (
                    <div className="flex items-center gap-2 text-[#A1A1AA] text-xs">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      {isAr ? 'يبحث في قاعدة البيانات…' : 'Querying database…'}
                    </div>
                  )}
                </div>

                {/* Composer */}
                <div className="border-t border-white/10 p-3 sm:p-4 bg-white/[0.02]">
                  <div className="flex items-end gap-2">
                    <textarea
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                      placeholder={isAr ? 'اسأل عن أي شيء في قاعدة البيانات…' : 'Ask anything about your data…'}
                      rows={1}
                      data-testid="rag-input"
                      dir={isAr ? 'rtl' : 'ltr'}
                      className="flex-1 resize-none bg-white/5 border border-white/10 text-white placeholder:text-[#71717A] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#d4af37]/60 max-h-32"
                    />
                    <button
                      onClick={() => handleSend()}
                      disabled={!input.trim() || sending}
                      data-testid="rag-send-btn"
                      className="bg-gradient-to-br from-[#d4af37] to-[#9b7d20] text-black h-10 w-10 rounded-xl flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105 transition-transform"
                      aria-label="Send"
                    >
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-[10px] text-[#71717A] mt-2 px-1">
                    {isAr
                      ? '⏎ للإرسال · Shift+⏎ لسطر جديد · Read-only — لن يعدّل النموذج أي بيانات'
                      : '⏎ to send · Shift+⏎ for new line · Read-only — model cannot modify data'}
                  </p>
                </div>
              </section>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ---------------- Sub-components ----------------
const MD_COMPONENTS = {
  table: (props) => (
    <table className="w-full border-collapse text-xs my-2" {...props} />
  ),
  th: (props) => (
    <th className="border border-white/10 px-2 py-1 bg-white/5 text-white text-start" {...props} />
  ),
  td: (props) => (
    <td className="border border-white/10 px-2 py-1 text-[#E5E5E5]" {...props} />
  ),
};

function Bubble({ msg, isAr }) {
  const isUser = msg.role === 'user';
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}
      data-testid={`rag-msg-${msg.role}`}
    >
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
          isUser ? 'bg-[#d4af37]/20 text-[#d4af37]' : msg.error ? 'bg-red-500/15 text-red-400' : 'bg-emerald-500/15 text-emerald-400'
        }`}
      >
        {isUser ? <UserIcon className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
      </div>
      <div className={`flex-1 min-w-0 ${isUser ? 'text-end' : 'text-start'}`}>
        <div
          className={`inline-block max-w-full px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
            isUser
              ? 'bg-[#d4af37]/15 text-white'
              : msg.error
              ? 'bg-red-500/10 border border-red-500/20 text-red-200'
              : 'bg-white/5 text-white'
          }`}
        >
          {isUser ? (
            <div className="whitespace-pre-wrap break-words">{msg.content}</div>
          ) : (
            <div className="prose prose-invert prose-sm max-w-none prose-table:text-xs prose-th:text-white prose-td:text-[#E5E5E5]">
              <ReactMarkdown components={MD_COMPONENTS}>
                {msg.content || ''}
              </ReactMarkdown>
            </div>
          )}
        </div>
        {msg.rows_count !== undefined && msg.rows_count !== null && (
          <div className={`mt-2 ${isUser ? 'text-end' : 'text-start'}`}>
            <span className="inline-flex items-center gap-1.5 text-[10px] text-[#A1A1AA] bg-white/[0.04] border border-white/10 rounded-full px-2 py-0.5">
              <Database className="h-3 w-3" />
              {isAr ? `${msg.rows_count} نتيجة` : `${msg.rows_count} rows`}
            </span>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function EmptyState({ isAr, onPick }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-4 py-10 max-w-lg mx-auto">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#d4af37]/30 to-[#9b7d20]/20 border border-[#d4af37]/30 flex items-center justify-center mb-4">
        <Sparkles className="h-8 w-8 text-[#d4af37]" />
      </div>
      <h3 className="text-white font-bold text-base mb-1">
        {isAr ? 'كيف يمكنني مساعدتك؟' : 'How can I help?'}
      </h3>
      <p className="text-xs text-[#A1A1AA] mb-6 leading-relaxed">
        {isAr
          ? 'اسألني بلغة طبيعية وسأبحث في قواعد بياناتك (قراءة فقط) وأجيبك. جرّب أحد الأمثلة:'
          : 'Ask me in natural language — I will query your databases (read-only) and answer. Try one of these:'}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
        {SUGGESTIONS.map((s, i) => {
          const q = isAr ? s.ar : s.en;
          return (
            <button
              key={i}
              onClick={() => onPick(q)}
              data-testid={`rag-suggestion-${i}`}
              className="text-start text-xs text-white bg-white/[0.03] hover:bg-white/[0.07] border border-white/10 hover:border-[#d4af37]/40 rounded-xl px-3 py-2.5 transition-colors"
            >
              {q}
            </button>
          );
        })}
      </div>
    </div>
  );
}
